/* One media element; neither video is requested until visible AND window loaded. */
(() => {
  'use strict';
  if (customElements.get('rg-film-banner')) return;
  customElements.define('rg-film-banner', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.video = this.querySelector('video');
      this.button = this.querySelector('.rg-film__toggle');
      if (!this.video || !this.button) return;
      this.mobile = matchMedia('(max-width: 989px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.loaded = document.readyState === 'complete';
      this.visible = false;
      this.intent = null;
      this.generation = 0;
      this.current = '';
      this.blocked = false;
      this.failed = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.button.addEventListener('click', () => {
        this.intent = this.video.paused ? 'play' : 'pause';
        this.blocked = false;
        this.failed = false;
        this.sync(true);
      }, { signal });
      this.video.addEventListener('playing', () => {
        if (!this.visible || document.hidden || this.intent === 'pause') {
          this.video.pause();
          return;
        }
        this.setAttribute('data-loaded', '');
        this.setPlaying(true);
      }, { signal });
      this.video.addEventListener('pause', () => this.setPlaying(false), { signal });
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.removeAttribute('data-loaded');
        this.setPlaying(false);
        this.updateButton();
      }, { signal });
      this.mobile.addEventListener('change', () => {
        this.blocked = false;
        this.failed = false;
        this.sync();
      }, { signal });
      this.motion.addEventListener('change', () => this.sync(), { signal });
      if (this.connection && this.connection.addEventListener) {
        this.connection.addEventListener('change', () => this.sync(), { signal });
      }
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      window.addEventListener('load', () => {
        this.loaded = true;
        this.sync();
      }, { once: true, signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting);
          this.sync();
        }, { threshold: 0.08 });
        this.observer.observe(this.querySelector('.rg-film__frame'));
      } else {
        /* Without IntersectionObserver: manual playback only. */
        this.manualOnly = true;
        this.visible = true;
      }
      this.updateButton();
    }
    source() {
      return (this.mobile.matches ? this.dataset.mobile : this.dataset.desktop) || '';
    }
    automaticAllowed() {
      return !this.manualOnly && !this.motion.matches && !this.connection?.saveData &&
        !/^(slow-2g|2g)$/.test(this.connection?.effectiveType || '');
    }
    setPlaying(playing) {
      this.toggleAttribute('data-playing', playing);
      this.button.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
    }
    updateButton() {
      this.button.hidden = !this.source();
      this.button.setAttribute('aria-label', this.failed ? this.dataset.retryLabel :
        (this.video.paused ? this.dataset.playLabel : this.dataset.pauseLabel));
    }
    async sync(manual = false) {
      const source = this.source();
      const changed = source !== this.current;
      if (changed && this.current) {
        this.generation++;
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
        this.current = '';
        this.removeAttribute('data-loaded');
        this.setPlaying(false);
      }
      this.updateButton();
      const canPlay = source && this.visible && !document.hidden && (this.loaded || manual) &&
        this.intent !== 'pause' && !this.failed && !this.blocked &&
        (this.intent === 'play' || this.automaticAllowed());
      if (!canPlay) {
        this.generation++;
        this.video.pause();
        return;
      }
      if (this.pending === source || (!this.video.paused && this.current === source)) return;
      if (this.current !== source) {
        this.current = source;
        this.video.src = source;
        this.video.load();
      }
      const generation = ++this.generation;
      this.pending = source;
      try {
        await this.video.play();
        if (!this.isConnected || generation !== this.generation) return;
      } catch (_) {
        if (generation === this.generation) {
          this.blocked = true;
          this.setPlaying(false);
        }
      } finally {
        if (this.pending === source) this.pending = '';
      }
    }
    disconnectedCallback() {
      this.generation++;
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.video?.pause();
      this.video?.removeAttribute('src');
      this.video?.load();
    }
  });
})();
